import { useEffect, useState } from "react";
import {
  getTimeEfficiencyKpis,
  getTimeEfficiencyOptions,
  getTimeEfficiencyProjectHistory,
  getTimeEfficiencyProjects,
  getTimeEfficiencyProjectsAboveAverage,
  getTimeEfficiencyProjectsWithReopening,
  getTimeEfficiencyRankings,
  getTimeEfficiencyReopenKpi,
  getTimeEfficiencyStageChart,
  getTimeEfficiencyStaleProjects,
} from "../../time-efficiency-live/api/timeEfficiency.api";
import type {
  TimeEfficiencyFilters as ApiFilters,
  TimeEfficiencyProjectItem,
} from "../../time-efficiency-live/types/timeEfficiency.types";
import { useQuery } from "react-query";
import { queryKeys } from "../../../queries/keys";
import type { FilterOption } from "../components/FilterBar";
import type {
  AndamentoProject,
  DateField,
  HistorySequenceRow,
  SortKey,
  TableRow,
} from "../types/timeEfficiency.types";
import { STAGE_COLOR, STAGE_ICON } from "../data/mockData";

const ROWS_PER_PAGE = 10;
const DEFAULT_START = "2026-04-01";
const DEFAULT_END = "2026-09-03";
const DEFAULT_SORT_KEY: SortKey = "tempoNoStatus";
const DEFAULT_SORT_DIR: 1 | -1 = -1;
// "Etapa mais demorada"/"Etapa com mais projetos parados agora" não
// consideram esses status como candidatos -- não são etapas de fluxo de
// verdade (feedback do usuário, #MMV-2615). O KPI "Etapa mais demorada" já
// aplica isso no backend (`getKpis`); "Etapa com mais projetos parados
// agora" é calculado aqui no front a partir de `stageChartRaw`, então
// precisa do mesmo filtro aplicado aqui também.
const EXCLUDED_STAGE_STATUS_VALUES = new Set([
  "APPROVED",
  "CANCELED",
  "LOST",
  "DECLINED",
  "AUDITED",
]);

function iconFor(label: string): string {
  if (STAGE_ICON[label]) return STAGE_ICON[label];
  const words = label.split(/\s+/).filter(Boolean);
  if (!words.length) return "–";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function formatDateBR(value: string | Date | null | undefined): string {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleDateString("pt-BR");
}

/** "MM26142"-style, igual ao formato usado no mock (`mockData.ts`), pra
 * manter a mesma aparência visual nos componentes que esperam esse id. */
function displayId(mmCode: number, yearCode: number): string {
  return `MM${mmCode}${String(yearCode).padStart(2, "0")}`;
}

function mapProjectItem(item: TimeEfficiencyProjectItem): AndamentoProject {
  const now = Date.now();
  const entrouEmDate = new Date(now - item.timeInCurrentStatusSeconds * 1000);
  // `stageFirstEnteredAt`/`stageTotalDurationSeconds` só vêm preenchidos
  // quando a consulta pediu `stageFocus` (drill-downs "Etapa mais
  // demorada"/"Etapa com mais projetos parados agora") -- nesse caso
  // "Entrou em"/"Tempo total" passam a significar a PRIMEIRA entrada nessa
  // etapa e o tempo TOTAL acumulado nela (somando reaberturas), não a
  // visita atual nem o projeto inteiro (bug real reportado pelo usuário).
  const hasStageFocus = item.stageFirstEnteredAt !== undefined;
  return {
    id: displayId(item.mmCode, item.yearCode),
    projectId: item.id,
    projeto:
      item.projectName ||
      item.clientName ||
      displayId(item.mmCode, item.yearCode),
    cliente: item.clientName || "–",
    celula: item.cellName || "–",
    coordenador: item.coordinatorName || undefined,
    tipoEvento: item.eventType || undefined,
    numPessoas: item.numberOfPeople,
    status: item.statusLabel,
    entrouEm: hasStageFocus
      ? formatDateBR(item.stageFirstEnteredAt)
      : formatDateBR(entrouEmDate),
    tempoNoStatus: item.timeInCurrentStatusSeconds / 86400,
    tempoNoStatusDisponivel: item.hasStatusHistory ?? true,
    tempoTotal: hasStageFocus
      ? (item.stageTotalDurationSeconds ?? 0) / 86400
      : item.totalDurationSeconds != null
      ? item.totalDurationSeconds / 86400
      : item.timeInCurrentStatusSeconds / 86400,
    inicio: formatDateBR(item.startDate),
    criacao: formatDateBR(item.createdAt),
    justificativa: item.justification || "–",
    reaberturas: item.reopenCount ?? 0,
  };
}

function mapToTableRow(item: TimeEfficiencyProjectItem): TableRow {
  const andamento = mapProjectItem(item);
  return {
    id: andamento.id,
    projectId: andamento.projectId,
    projeto: andamento.projeto,
    cliente: andamento.cliente,
    status: andamento.status,
    entrouEm: andamento.entrouEm,
    tempoNoStatus: andamento.tempoNoStatus,
    tempoNoStatusDisponivel: andamento.tempoNoStatusDisponivel,
    tempoTotal: andamento.tempoTotal,
    inicio: andamento.inicio,
    criacao: andamento.criacao,
    justificativa: andamento.justificativa,
    reaberturas: andamento.reaberturas ?? 0,
    tipoEvento: andamento.tipoEvento || "",
    numPessoas: andamento.numPessoas ?? null,
  };
}

/** Dados reais (Api) pros painéis que já têm endpoint pronto: filtros,
 * os 3 KPIs originais, gráfico de etapas, lista de parados e tabela.
 * Alimenta os MESMOS componentes visuais do porte mockado (layout
 * intocado) -- só os valores mudam. */
export function useTimeEfficiencyRealData(options?: {
  /** Drill-down do KPI "Projetos acima da média" (#MMV-2615): quando
   * ativo, a tabela troca a fonte pra concluídos com duração total acima
   * da média do conjunto filtrado, em vez da lista padrão "em andamento". */
  aboveAverageActive?: boolean;
  /** Drill-down do KPI "Etapa com mais projetos parados agora": quando
   * ativo, força o filtro de Status atual pra etapa com mais projetos em
   * andamento parados nela neste momento (`presoAgora`). */
  etapaParadaActive?: boolean;
  /** Drill-down do KPI "Etapa mais demorada": quando ativo, força o filtro
   * de Status atual pra etapa mais demorada entre os concluídos
   * (`kpis.slowest`) -- mesma tabela/mecanismo do drill-down acima. */
  etapaLentaActive?: boolean;
  /** Drill-down do KPI "Projetos com reabertura": quando ativo, a tabela
   * troca a fonte pra projetos (concluídos + em andamento) que voltaram a
   * um status que já tinham passado. */
  reopenActive?: boolean;
}) {
  const aboveAverageActive = options?.aboveAverageActive ?? false;
  const etapaParadaActive = options?.etapaParadaActive ?? false;
  const etapaLentaActive = options?.etapaLentaActive ?? false;
  const reopenActive = options?.reopenActive ?? false;
  // `filtroCelula`/`filtroCoordenador`/etc. são o RASCUNHO -- o que os
  // campos da `FilterBar` mostram e vão acumulando enquanto o usuário
  // mexe neles. `applied` é o que REALMENTE alimenta `apiFilters` (e,
  // portanto, as consultas) -- só muda quando o usuário clica "Aplicar
  // filtro". Sem essa separação, cada campo (célula, status, datas...)
  // disparava uma rajada de consultas por conta própria a cada mudança --
  // bug real reportado pelo usuário, mais visível no filtro de data (que
  // também aciona o `stage-chart`, mais pesado). `applied` guarda só os
  // campos que passam por esse "portão" -- busca e paginação continuam
  // tendo seu próprio debounce/efeito, não fazem parte do Aplicar.
  const [filtroCelula, setFiltroCelula] = useState("");
  const [filtroCoordenador, setFiltroCoordenador] = useState("");
  const [filtroTipoEvento, setFiltroTipoEvento] = useState("");
  const [filtroStatus, setFiltroStatus] = useState<Set<string>>(new Set());
  const [filtroCampoData, setFiltroCampoData] = useState<DateField>("inicio");
  const [dataInicio, setDataInicio] = useState(DEFAULT_START);
  const [dataFim, setDataFim] = useState(DEFAULT_END);
  const [applied, setApplied] = useState({
    celula: "",
    coordenador: "",
    tipoEvento: "",
    status: new Set<string>(),
    campoData: "inicio" as DateField,
    dataInicio: DEFAULT_START,
    dataFim: DEFAULT_END,
  });
  // `searchInput` é o que o campo mostra (atualiza a cada tecla);
  // `searchTerm` é o que realmente vai pro filtro da Api, só depois que o
  // usuário para de digitar -- sem isso, cada letra digitada disparava uma
  // chamada nova pro mesmo endpoint.
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey>(DEFAULT_SORT_KEY);
  const [sortDir, setSortDir] = useState<1 | -1>(DEFAULT_SORT_DIR);
  // Modal "Histórico de status" aberto a partir do número na coluna
  // Reaberturas -- `null` quando fechado, id real do projeto quando aberto.
  const [historyProjectId, setHistoryProjectId] = useState<string | null>(null);
  const [historyDisplayId, setHistoryDisplayId] = useState<string | null>(null);

  const apiFilters: ApiFilters = {
    cellId: applied.celula || undefined,
    coordinatorId: applied.coordenador || undefined,
    eventType: applied.tipoEvento || undefined,
    status: applied.status.size ? Array.from(applied.status) : undefined,
    dateField: applied.campoData === "criacao" ? "createdAt" : "startDate",
    startDate: applied.dataInicio,
    endDate: applied.dataFim,
    search: searchTerm || undefined,
    page,
    pageSize: ROWS_PER_PAGE,
  };
  // KPIs ignoram o filtro de status, igual ao Gherkin do #MMV-2615, e
  // também `page`/`pageSize`/`search` -- kpis/reopen-kpi nunca paginam nem
  // filtram por busca, olham o conjunto inteiro sempre. Sem isso, trocar de
  // página OU digitar na busca da tabela disparava um refetch de ~20s
  // desses painéis à toa (bug real de performance, não só estético -- a
  // chave da query incluía campos que não deviam afetar esses endpoints).
  const {
    status: _status,
    page: _page,
    pageSize: _pageSize,
    search: _search,
    ...kpiFilters
  } = apiFilters;
  // Mantém `status` e `page`/`pageSize`, sem `search` -- usado pelos
  // drill-downs "acima da média"/"com reabertura" (a Api não suporta busca
  // nesses dois hoje; se precisar, dá pra reconsiderar depois).
  const {
    status: _status2,
    search: _search2,
    ...pagedNoStatusFilters
  } = apiFilters;
  // Mantém `status`, sem `page`/`pageSize`/`search` -- usado pela lista de
  // parados e pelo lado "em andamento" do gráfico de etapas (não paginam
  // nem filtram por busca).
  const {
    page: _page2,
    pageSize: _pageSize2,
    search: _search3,
    ...nonPagedFilters
  } = apiFilters;

  const { data: optionsData } = useQuery(
    queryKeys.timeEfficiencyLiveOptions,
    getTimeEfficiencyOptions
  );
  // `keepPreviousData` nos 5 painéis abaixo (igual ao que a tabela já
  // fazia): sem isso, mudar QUALQUER filtro (célula, status, datas) fazia
  // `data` virar `undefined` por um instante em troca de chave de query,
  // e cada painel caía pro próprio estado de loading (tela em branco/
  // spinner) até a resposta nova chegar. Com `keepPreviousData`, o painel
  // continua mostrando o valor anterior (sem "sumir") enquanto a nova
  // resposta não chega -- só que `isLoading` fica `false` nesse caso (já
  // tem dado), então usar `isLoading` pro spinner escondia que uma consulta
  // está rodando (bug real reportado pelo usuário: "chama o endpoint mas
  // não aparece o loading"). `isFetching` é `true` tanto no primeiro
  // carregamento quanto em qualquer refetch em segundo plano -- é esse que
  // os cards usam pro spinner.
  const { data: kpisRaw, isFetching: kpisLoading } = useQuery(
    queryKeys.timeEfficiencyLiveKpis(kpiFilters),
    () => getTimeEfficiencyKpis(kpiFilters as ApiFilters),
    { keepPreviousData: true }
  );
  const { data: stageChartRaw, isFetching: stageChartLoading } = useQuery(
    queryKeys.timeEfficiencyLiveStageChart(nonPagedFilters),
    () => getTimeEfficiencyStageChart(nonPagedFilters as ApiFilters),
    { keepPreviousData: true }
  );
  const { data: staleRaw, isFetching: staleLoading } = useQuery(
    queryKeys.timeEfficiencyLiveStaleProjects(nonPagedFilters),
    () => getTimeEfficiencyStaleProjects(nonPagedFilters as ApiFilters),
    { keepPreviousData: true }
  );
  const { data: reopenKpiRaw, isFetching: reopenKpiLoading } = useQuery(
    queryKeys.timeEfficiencyLiveReopenKpi(kpiFilters),
    () => getTimeEfficiencyReopenKpi(kpiFilters as ApiFilters),
    { keepPreviousData: true }
  );
  const { data: rankingsRaw, isFetching: rankingsLoading } = useQuery(
    queryKeys.timeEfficiencyLiveRankings(nonPagedFilters),
    () => getTimeEfficiencyRankings(nonPagedFilters as ApiFilters),
    { keepPreviousData: true }
  );

  // "Etapa com mais projetos parados agora": a etapa com mais projetos em
  // andamento parados nela neste momento -- já vem de `stageChartRaw`
  // (`inProgressCount`/`inProgressAverageSeconds`), sem precisar de
  // endpoint novo. Exclui os status que não são etapas de fluxo de verdade
  // (feedback do usuário, #MMV-2615).
  const presoAgoraRaw = (stageChartRaw ?? [])
    .filter(
      (e) =>
        e.inProgressCount > 0 &&
        !EXCLUDED_STAGE_STATUS_VALUES.has(e.statusValue)
    )
    .sort((a, b) => b.inProgressCount - a.inProgressCount)[0];
  const presoAgora = presoAgoraRaw
    ? {
        etapa: presoAgoraRaw.statusLabel,
        etapaValue: presoAgoraRaw.statusValue,
        count: presoAgoraRaw.inProgressCount,
        media: (presoAgoraRaw.inProgressAverageSeconds ?? 0) / 86400,
      }
    : null;

  // Valor de status do drill-down de etapa ativo no momento (se algum) --
  // usado tanto pra forçar o filtro de Status atual quanto pra pedir à Api
  // (via `stageFocus`) a primeira entrada + tempo total acumulado NESSA
  // etapa (ver `mapProjectItem`).
  const activeStageFilterType: "etapaParada" | "etapaLenta" | null =
    etapaParadaActive ? "etapaParada" : etapaLentaActive ? "etapaLenta" : null;
  const activeStageFilterValue =
    activeStageFilterType === "etapaParada"
      ? presoAgora?.etapaValue
      : activeStageFilterType === "etapaLenta"
      ? kpisRaw?.slowestStage?.statusValue
      : undefined;

  const {
    data: projectsRaw,
    isLoading: projectsLoading,
    isFetching: projectsFetching,
  } = useQuery(
    reopenActive
      ? queryKeys.timeEfficiencyLiveProjectsWithReopening(pagedNoStatusFilters)
      : aboveAverageActive
      ? queryKeys.timeEfficiencyLiveProjectsAboveAverage(pagedNoStatusFilters)
      : queryKeys.timeEfficiencyLiveProjects({
          ...apiFilters,
          stageFocus: activeStageFilterValue,
        }),
    () =>
      reopenActive
        ? getTimeEfficiencyProjectsWithReopening(
            pagedNoStatusFilters as ApiFilters
          )
        : aboveAverageActive
        ? getTimeEfficiencyProjectsAboveAverage(
            pagedNoStatusFilters as ApiFilters
          )
        : getTimeEfficiencyProjects({
            ...apiFilters,
            stageFocus: activeStageFilterValue,
          }),
    { keepPreviousData: true }
  );
  const { data: historyRaw, isLoading: historyLoading } = useQuery(
    queryKeys.timeEfficiencyLiveProjectHistory(historyProjectId ?? ""),
    () => getTimeEfficiencyProjectHistory(historyProjectId as string),
    { enabled: Boolean(historyProjectId) }
  );

  function toOptions(
    list: Array<{ id: string; name: string }> | undefined
  ): FilterOption[] {
    return (list ?? []).map((o) => ({ value: o.id, label: o.name }));
  }

  const stageMaxShared = Math.max(
    1,
    ...(stageChartRaw ?? []).map((e) =>
      Math.max(e.concludedAverageSeconds ?? 0, e.inProgressAverageSeconds ?? 0)
    )
  );
  const stageChartData = (stageChartRaw ?? []).map((e, i) => ({
    nome: e.statusLabel,
    icon: iconFor(e.statusLabel),
    colorIndex: i % STAGE_COLOR.length,
    avgConcluidos:
      e.concludedAverageSeconds != null
        ? e.concludedAverageSeconds / 86400
        : null,
    countConcluidos: e.concludedCount,
    avgAndamento:
      e.inProgressAverageSeconds != null
        ? e.inProgressAverageSeconds / 86400
        : null,
    countAndamento: e.inProgressCount,
    maxShared: stageMaxShared / 86400,
  }));

  // Debounce da busca: só aplica o texto digitado ao filtro (e refaz a
  // consulta) 500ms depois da última tecla.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(0);
    }, 500);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Ao ligar um drill-down que troca a fonte da tabela ("acima da média" ou
  // "com reabertura"), começa da página 1 e ordena pelo campo relevante --
  // mesmo comportamento do clique no card no mockup de referência. Ao
  // desligar os dois, volta pro estado padrão original da tabela.
  useEffect(() => {
    setPage(0);
    if (aboveAverageActive || etapaLentaActive) {
      setSortKey("tempoTotal");
      setSortDir(-1);
    } else if (reopenActive) {
      setSortKey("reaberturas");
      setSortDir(-1);
    } else if (etapaParadaActive) {
      setSortKey("tempoNoStatus");
      setSortDir(-1);
    } else {
      setSortKey(DEFAULT_SORT_KEY);
      setSortDir(DEFAULT_SORT_DIR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aboveAverageActive, etapaParadaActive, etapaLentaActive, reopenActive]);

  // Ao ligar um drill-down de etapa ("etapa parada" ou "etapa mais
  // demorada"), força o filtro de Status atual pra essa etapa (reaproveita
  // a lista padrão, só filtrada). Consolidado num único efeito (em vez de
  // um par liga/desliga por drill-down) porque os dois mexem no MESMO
  // estado (`filtroStatus`) -- dois efeitos separados corriam risco da
  // mesma race de antes (um desligando o filtro bem na hora que o outro
  // tentava ligar). `activeStageFilterValue` muda toda vez que QUALQUER
  // filtro muda (célula, data etc. afetam `stageChartRaw`/`kpisRaw`) --
  // por isso só aplica enquanto o tipo de drill-down realmente for de
  // etapa, senão qualquer escolha no filtro reaplicaria/apagaria o Status
  // atual que o usuário acabou de selecionar (bug real já visto antes).
  // Grava tanto no rascunho (`filtroStatus`, pra refletir no select) quanto
  // em `applied` diretamente -- um clique num card de KPI deve filtrar na
  // hora, sem esperar o usuário clicar em "Aplicar filtro".
  useEffect(() => {
    if (activeStageFilterType && activeStageFilterValue) {
      const next = new Set([activeStageFilterValue]);
      setFiltroStatus(next);
      setApplied((prev) => ({ ...prev, status: next }));
      setPage(0);
    } else if (!activeStageFilterType) {
      setFiltroStatus(new Set());
      setApplied((prev) => ({ ...prev, status: new Set() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStageFilterType, activeStageFilterValue]);

  const toRankingItems = (
    entries: Array<{ name: string; valueSeconds: number; projectCount: number }>
  ) =>
    entries.map((e) => ({
      nome: e.name,
      media: e.valueSeconds / 86400,
      count: e.projectCount,
    }));
  const celulaRanking = toRankingItems(rankingsRaw?.byCell ?? []);
  const coordenadorRanking = toRankingItems(rankingsRaw?.byCoordinator ?? []);
  const tipoEventoRanking = toRankingItems(rankingsRaw?.byEventType ?? []);

  const staleProjects = (staleRaw ?? []).map(mapProjectItem);
  const historySequencia: HistorySequenceRow[] = (
    historyRaw?.history ?? []
  ).map((row) => ({
    status: row.statusLabel,
    entrouEm: row.enteredAtLabel,
    saiuEm: row.exitedAtLabel,
    duracaoTexto: row.durationLabel,
    ocorrencia: row.occurrence,
    justificativa: row.justification ?? null,
  }));
  // Ordenação só na página atual (já veio paginada do servidor) -- a Api
  // ainda só ordena por tempo no status atual internamente.
  const tableRows = [...(projectsRaw?.items ?? []).map(mapToTableRow)].sort(
    (a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === "number" && typeof vb === "number")
        return sortDir * (va - vb);
      return sortDir * String(va).localeCompare(String(vb));
    }
  );
  const totalRows = projectsRaw?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / ROWS_PER_PAGE));

  const kpis = {
    hasConcluded: kpisRaw?.hasConcludedProjects ?? false,
    concludedCount: kpisRaw?.concludedProjectsCount ?? 0,
    tempoMedioTotal: (kpisRaw?.averageTotalDurationSeconds ?? 0) / 86400,
    mediana: (kpisRaw?.medianTotalDurationSeconds ?? 0) / 86400,
    slowest: kpisRaw?.slowestStage
      ? {
          etapa: kpisRaw.slowestStage.statusLabel,
          // Inclui o value (a Api usa, o label não) -- pro handler de
          // clique do card "Etapa mais demorada" montar o drill-down.
          etapaValue: kpisRaw.slowestStage.statusValue,
          media: kpisRaw.slowestStage.averageDurationSeconds / 86400,
        }
      : null,
    acimaMedia: kpisRaw?.aboveAverageCount ?? 0,
    pctAcimaMedia: kpisRaw?.aboveAveragePercentage ?? 0,
    presoAgora: presoAgora
      ? {
          etapa: presoAgora.etapa,
          count: presoAgora.count,
          media: presoAgora.media,
        }
      : null,
    comReabertura: reopenKpiRaw?.withReopenCount ?? 0,
    maxReaberturas: reopenKpiRaw?.maxReopenCount ?? 0,
    pctComReabertura: reopenKpiRaw?.withReopenPercentage ?? 0,
    totalNoFiltroReab: reopenKpiRaw?.totalCount ?? 0,
  };

  return {
    // Rascunho dos campos -- só mexe no que a `FilterBar` mostra, não
    // dispara nenhuma consulta. Só `applyFilters` (botão "Aplicar filtro")
    // e `clearFilters` (botão "Limpar") de fato mudam `applied`.
    filtroCelula,
    setFiltroCelula,
    filtroCoordenador,
    setFiltroCoordenador,
    filtroTipoEvento,
    setFiltroTipoEvento,
    filtroStatus,
    setFiltroStatus,
    filtroCampoData,
    setFiltroCampoData,
    dataInicio,
    setDataInicio,
    dataFim,
    setDataFim,
    // O campo mostra `searchInput` (atualiza na hora); o filtro de verdade
    // só muda depois do debounce (ver efeito acima) -- a busca não passa
    // pelo botão "Aplicar filtro", só os campos da `FilterBar`.
    searchTerm: searchInput,
    setSearchTerm: (v: string) => {
      setSearchInput(v);
    },
    // Botão "Aplicar filtro": copia o rascunho pra `applied`, que é o que
    // realmente alimenta as consultas.
    applyFilters: () => {
      setApplied({
        celula: filtroCelula,
        coordenador: filtroCoordenador,
        tipoEvento: filtroTipoEvento,
        status: filtroStatus,
        campoData: filtroCampoData,
        dataInicio,
        dataFim,
      });
      setPage(0);
    },
    clearFilters: () => {
      setFiltroCelula("");
      setFiltroCoordenador("");
      setFiltroTipoEvento("");
      setFiltroStatus(new Set());
      setFiltroCampoData("inicio");
      setDataInicio(DEFAULT_START);
      setDataFim(DEFAULT_END);
      setSearchInput("");
      setSearchTerm("");
      // "Limpar" reseta e aplica na hora -- diferente de um campo normal,
      // não faz sentido exigir um segundo clique em "Aplicar filtro" só
      // pra voltar ao estado sem filtro nenhum.
      setApplied({
        celula: "",
        coordenador: "",
        tipoEvento: "",
        status: new Set(),
        campoData: "inicio",
        dataInicio: DEFAULT_START,
        dataFim: DEFAULT_END,
      });
      setPage(0);
    },

    celulaOptions: toOptions(optionsData?.cells),
    coordenadorOptions: toOptions(optionsData?.coordinators),
    tipoEventoOptions: (optionsData?.eventTypes ?? []).map((t) => ({
      value: t,
      label: t,
    })),
    statusOptions: (optionsData?.statusOptions ?? []).map((s) => ({
      value: s.value,
      label: s.label,
    })),

    kpis,
    kpisLoading,
    stageChartData,
    stageChartLoading,
    staleProjects,
    staleLoading,
    reopenKpiLoading,
    celulaRanking,
    coordenadorRanking,
    tipoEventoRanking,
    rankingsLoading,
    coordenadorHasAnyData: (optionsData?.coordinators.length ?? 0) > 0,
    tipoEventoHasAnyData: (optionsData?.eventTypes.length ?? 0) > 0,
    // Inclui `etapaValue` (a Api usa, o label não) -- pro handler de clique
    // do card "Etapa com mais projetos parados agora" montar o drill-down.
    presoAgora,

    tableRows,
    totalRows,
    totalPages,
    currentPage: page + 1,
    projectsLoading,
    // true durante qualquer fetch (incluindo troca de modo/página com
    // `keepPreviousData`, que mantém as linhas antigas visíveis até a nova
    // resposta chegar) -- usado pra mostrar loading sem a tabela "sumir".
    projectsFetching,
    // A Api já calcula Tempo Total/Reaberturas de verdade em todos os
    // modos (tabela padrão incluída, não só nos drill-downs).
    tempoTotalAvailable: true,
    reaberturasAvailable: true,
    goToPrevPage: () => setPage((p) => Math.max(0, p - 1)),
    goToNextPage: () => setPage((p) => Math.min(totalPages - 1, p + 1)),

    sortKey,
    sortDir,
    toggleSort: (key: SortKey) => {
      setSortDir((prev) => (sortKey === key ? (-prev as 1 | -1) : -1));
      setSortKey(key);
    },

    // Modal "Histórico de status" (coluna Reaberturas).
    historyProjectId,
    historyDisplayId,
    historySequencia,
    historyLoading,
    openHistory: (projectId: string, displayId: string) => {
      setHistoryProjectId(projectId);
      setHistoryDisplayId(displayId);
    },
    closeHistory: () => {
      setHistoryProjectId(null);
      setHistoryDisplayId(null);
    },
  };
}
